// src/wallpapers/Ringtones.jsx
import { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Swal from 'sweetalert2';
import {
    FiSearch,
    FiEdit,
    FiTrash2,
    FiPlus,
    FiUpload,
    FiEye,
    FiEyeOff,
    FiFile,
    FiCheck,
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiMusic,
    FiPlay,
    FiPause,
    FiVolume2,
    FiVolumeX,
    FiRepeat,
    FiShuffle,
    FiSkipBack,
    FiSkipForward,
    FiMaximize2,
    FiMinimize2,
    FiHeadphones,
    FiMic,
    FiFolder,
    FiGrid,
    FiList
} from "react-icons/fi";
import PageHeader from "../components/PageHeader";
import CustomDataTable from "../components/DataTable";
import TagsInput from "../components/TagsInput";
import "./Ringtones.css";

// Audio file types
const AUDIO_TYPES = [
    'audio/mpeg',
    'audio/mp3',
    'audio/wav',
    'audio/ogg',
    'audio/aac',
    'audio/flac',
    'audio/webm',
    'audio/m4a',
    'audio/opus'
];

const AUDIO_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.aac', '.flac', '.webm', '.m4a', '.opus'];

export default function Ringtones({ darkMode }) {
    const [show, setShow] = useState(false);
    const [ringtoneData, setRingtoneData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [updatingStatusId, setUpdatingStatusId] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRingtone, setEditingRingtone] = useState(null);
    const [editForm, setEditForm] = useState({
        name: "",
        category: "ringtone",
        subCategory: "devotional",
        tags: [],
        status: "unpublished",
        duration: "",
        artist: "",
        audioFile: null,
        audioPreview: null,
        audioUrl: null // Store the uploaded URL from response
    });
    const [editLoading, setEditLoading] = useState(false);
    const [isFileUploading, setIsFileUploading] = useState(false);
    const [isFileUploaded, setIsFileUploaded] = useState(false);
    const [mediaName, setMediaName] = useState('');
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewRingtone, setPreviewRingtone] = useState(null);
    const [fileUploadError, setFileUploadError] = useState(null);
    const [bulkFiles, setBulkFiles] = useState([]);
    const [bulkForm, setBulkForm] = useState({
        category: "ringtone",
        subCategory: "devotional",
        status: "unpublished",
        artist: "",
        tags: []
    });
    const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
    const [bulkFileError, setBulkFileError] = useState(null);
    const [bulkViewMode, setBulkViewMode] = useState('grid');

    // Audio Player States
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [isMuted, setIsMuted] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);
    const [isShuffle, setIsShuffle] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [waveData, setWaveData] = useState([]);

    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const fileInputRef = useRef(null);
    const bulkFileInputRef = useRef(null);

    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [search, setSearch] = useState("");

    // ===== CATEGORY OPTIONS =====
    const categoryOptions = [
        { value: "ringtone", label: "📱 Ringtone" },
        { value: "mantra", label: "🕉️ Mantra" },
        { value: "shloka", label: "📖 Shloka" },
        { value: "chalisa", label: "🙏 Chalisa" },
        { value: "bhajan", label: "🎵 Bhajan" },
        { value: "aarti", label: "🪔 Aarti" },
        { value: "stotram", label: "📿 Stotram" },
        { value: "kirtan", label: "🎶 Kirtan" },
        { value: "meditation", label: "🧘 Meditation" },
        { value: "nature", label: "🌿 Nature" },
    ];

    const subCategoryOptions = {
        ringtone: [
            { value: "devotional", label: "Devotional" },
            { value: "classic", label: "Classic" },
            { value: "modern", label: "Modern" },
            { value: "instrumental", label: "Instrumental" },
        ],
        mantra: [
            { value: "gayatri", label: "Gayatri Mantra" },
            { value: "maha-mrityunjaya", label: "Maha Mrityunjaya" },
            { value: "hanuman", label: "Hanuman Mantra" },
            { value: "ganesh", label: "Ganesh Mantra" },
            { value: "shiva", label: "Shiva Mantra" },
            { value: "lakshmi", label: "Lakshmi Mantra" },
            { value: "saraswati", label: "Saraswati Mantra" },
        ],
        shloka: [
            { value: "bhagavad-gita", label: "Bhagavad Gita" },
            { value: "vedic", label: "Vedic Shlokas" },
            { value: "upanishad", label: "Upanishads" },
            { value: "puranic", label: "Puranic Shlokas" },
        ],
        chalisa: [
            { value: "hanuman-chalisa", label: "Hanuman Chalisa" },
            { value: "sunderkand", label: "Sunderkand" },
            { value: "ram-chalisa", label: "Ram Chalisa" },
            { value: "shiv-chalisa", label: "Shiv Chalisa" },
            { value: "durga-chalisa", label: "Durga Chalisa" },
        ],
        bhajan: [
            { value: "krishna", label: "Krishna Bhajan" },
            { value: "shiva", label: "Shiva Bhajan" },
            { value: "ram", label: "Ram Bhajan" },
            { value: "durga", label: "Durga Bhajan" },
            { value: "ganesh", label: "Ganesh Bhajan" },
            { value: "sai", label: "Sai Bhajan" },
        ],
        aarti: [
            { value: "ganesh-aarti", label: "Ganesh Aarti" },
            { value: "shiv-aarti", label: "Shiv Aarti" },
            { value: "durga-aarti", label: "Durga Aarti" },
            { value: "lakshmi-aarti", label: "Lakshmi Aarti" },
            { value: "sai-aarti", label: "Sai Aarti" },
        ],
        stotram: [
            { value: "shiva-stotram", label: "Shiva Stotram" },
            { value: "vishnu-stotram", label: "Vishnu Stotram" },
            { value: "devi-stotram", label: "Devi Stotram" },
        ],
        kirtan: [
            { value: "hare-krishna", label: "Hare Krishna" },
            { value: "nirgun", label: "Nirgun Kirtan" },
            { value: "sargun", label: "Sargun Kirtan" },
        ],
        meditation: [
            { value: "guided", label: "Guided Meditation" },
            { value: "music", label: "Meditation Music" },
            { value: "mindfulness", label: "Mindfulness" },
        ],
        nature: [
            { value: "rain", label: "Rain Sounds" },
            { value: "forest", label: "Forest Sounds" },
            { value: "ocean", label: "Ocean Waves" },
            { value: "birds", label: "Bird Sounds" },
        ],
    };

    // ===== DUMMY DATA (Fallback) =====
    const dummyRingtones = [
        {
            _id: "1",
            name: "Om Namah Shivaya",
            category: "mantra",
            subCategory: "shiva",
            artist: "Traditional",
            duration: "3:45",
            url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
            status: "published",
            tags: ["shiva", "devotional", "mantra"],
            createdAt: "2024-01-15T10:30:00Z"
        },
        // ... more dummy data
    ];

    // ===== FILTERED DATA =====
    const filteredData = ringtoneData.filter(item => {
        const searchableText = [
            item.name,
            item.category,
            item.subCategory || item.sub_category,
            item.artist,
            ...(Array.isArray(item.tags) ? item.tags : []),
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return searchableText.includes(search.toLowerCase());
    });

    // ===== STATS =====
    const totalRingtones = ringtoneData.length;
    const publishedCount = ringtoneData.filter(item => item.status === 'published').length;
    const unpublishedCount = ringtoneData.filter(item => item.status === 'unpublished').length;
    const categories = [...new Set(ringtoneData.map(item => item.category))];

    // ===== WAVE ANIMATION =====
    const generateWaveData = () => {
        const data = [];
        const bars = 60;
        for (let i = 0; i < bars; i++) {
            const height = 20 + Math.random() * 60;
            data.push(height);
        }
        return data;
    };

    const drawWave = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        const barWidth = (width / waveData.length) * 0.8;
        const gap = (width / waveData.length) * 0.2;

        ctx.clearRect(0, 0, width, height);

        const progress = duration > 0 ? currentTime / duration : 0;
        const progressIndex = Math.floor(progress * waveData.length);

        waveData.forEach((barHeight, index) => {
            const x = index * (barWidth + gap);
            const y = (height - barHeight) / 2;

            const isPlayed = index <= progressIndex;

            let color;
            if (isPlayed) {
                color = darkMode ? '#818cf8' : '#6366f1';
            } else {
                color = darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
            }

            ctx.shadowColor = isPlayed ? 'rgba(99, 102, 241, 0.3)' : 'transparent';
            ctx.shadowBlur = isPlayed ? 8 : 0;

            const radius = 3;
            ctx.beginPath();
            ctx.moveTo(x + radius, y);
            ctx.lineTo(x + barWidth - radius, y);
            ctx.quadraticCurveTo(x + barWidth, y, x + barWidth, y + radius);
            ctx.lineTo(x + barWidth, y + barHeight - radius);
            ctx.quadraticCurveTo(x + barWidth, y + barHeight, x + barWidth - radius, y + barHeight);
            ctx.lineTo(x + radius, y + barHeight);
            ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
            ctx.lineTo(x, y + radius);
            ctx.quadraticCurveTo(x, y, x + radius, y);
            ctx.closePath();

            ctx.fillStyle = color;
            ctx.fill();
            ctx.shadowBlur = 0;
        });

        if (progress > 0 && progress < 1) {
            const lineX = progress * width;
            ctx.beginPath();
            ctx.moveTo(lineX, 0);
            ctx.lineTo(lineX, height);
            ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)';
            ctx.lineWidth = 1;
            ctx.setLineDash([4, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
        }
    };

    useEffect(() => {
        if (previewModalOpen && previewRingtone) {
            setWaveData(generateWaveData());
            const interval = setInterval(() => {
                if (isPlaying) {
                    setWaveData(prev => {
                        const newData = [...prev];
                        for (let i = 0; i < 3; i++) {
                            const randomIndex = Math.floor(Math.random() * newData.length);
                            newData[randomIndex] = 20 + Math.random() * 60;
                        }
                        return newData;
                    });
                }
            }, 200);
            return () => clearInterval(interval);
        }
    }, [previewModalOpen, previewRingtone, isPlaying]);

    useEffect(() => {
        if (previewModalOpen && waveData.length > 0) {
            drawWave();
        }
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [waveData, currentTime, duration, darkMode, previewModalOpen]);

    // ============================================
    // ===== API CALL FUNCTIONS =====
    // ============================================

    // ===== FETCH RINGTONES =====
    const fetchRingtones = async () => {
        try {
            setLoading(true);
            
            const base_url = process.env.REACT_APP_ENV === 'local'
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const res = await fetch(`${base_url}/api/home/admin-media-data`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({ type: "audio" }),
            });

            if (!res.ok) throw new Error(`Fetch failed ${res.status}`);
            
            const json = await res.json();
            const list = json.data || json;
            
            const mappedData = Array.isArray(list) ? list.map(item => ({
                _id: item._id,
                name: item.display_name ? item.display_name : item.name,
                category: item.category || "ringtone",
                subCategory: item.sub_category || item.subCategory || "",
                artist: item.artist || "",
                duration: item.duration || "0:00",
                url: item.url || "",
                status: item.status || "unpublished",
                tags: Array.isArray(item.tags) ? item.tags : [],
                type: item.type || "audio",
                createdAt: item.createdAt || new Date().toISOString()
            })) : [];

            setRingtoneData(mappedData);
            
            if (mappedData.length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: 'No Audio Found',
                    text: 'No audio files found. Add some using the "Add Audio" button.',
                    timer: 3000,
                    showConfirmButton: true,
                    confirmButtonColor: '#6366f1',
                });
            }

        } catch (err) {
            console.error('Failed to fetch ringtones:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Data',
                text: 'Using dummy data as fallback. Please try again later.',
                timer: 3000,
                showConfirmButton: true,
                confirmButtonColor: '#6366f1',
            });
            
            setRingtoneData(dummyRingtones);
        } finally {
            setLoading(false);
        }
    };

    // ===== CREATE RINGTONE =====
    const createRingtone = async (data) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/submit-media`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_AUTH_TOKEN || ''}`
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) throw new Error(`Create failed ${response.status}`);

            const result = await response.json();
            
            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Audio file created successfully!',
                timer: 2000,
                showConfirmButton: false,
            });

            await fetchRingtones();
            
            return result;

        } catch (err) {
            console.error('Failed to create ringtone:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Create',
                text: err.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
            throw err;
        }
    };

    // ===== UPDATE RINGTONE =====
    const updateRingtone = async (id, data) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/admin-media-data/${id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) throw new Error(`Update failed ${response.status}`);

            const result = await response.json();
            
            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: 'Audio file updated successfully!',
                timer: 2000,
                showConfirmButton: false,
            });

            await fetchRingtones();
            
            return result;

        } catch (err) {
            console.error('Failed to update ringtone:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Update',
                text: err.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
            throw err;
        }
    };

    // ===== DELETE RINGTONE =====
    const deleteRingtone = async (id) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/admin-media-data/${id}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                }
            });

            if (!response.ok) throw new Error(`Delete failed ${response.status}`);

            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'Audio file deleted successfully!',
                timer: 2000,
                showConfirmButton: false,
            });

            await fetchRingtones();

        } catch (err) {
            console.error('Failed to delete ringtone:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Delete',
                text: err.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
            throw err;
        }
    };

    // ===== BULK DELETE =====
    const bulkDeleteRingtones = async (ids) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/admin-media-data/bulk`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({ ids })
            });

            if (!response.ok) throw new Error(`Bulk delete failed ${response.status}`);

            Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: `${ids.length} audio files deleted successfully!`,
                timer: 2000,
                showConfirmButton: false,
            });

            await fetchRingtones();

        } catch (err) {
            console.error('Failed to bulk delete:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Delete',
                text: err.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
            throw err;
        }
    };

    // ===== BULK UPDATE STATUS =====
    const bulkUpdateStatus = async (ids, status) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/admin-media-data/bulk-status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({ ids, status })
            });

            if (!response.ok) throw new Error(`Bulk update failed ${response.status}`);

            Swal.fire({
                icon: 'success',
                title: 'Updated!',
                text: `${ids.length} audio files ${status === 'published' ? 'published' : 'unpublished'} successfully!`,
                timer: 2000,
                showConfirmButton: false,
            });

            await fetchRingtones();

        } catch (err) {
            console.error('Failed to bulk update:', err);
            
            Swal.fire({
                icon: 'error',
                title: 'Failed to Update',
                text: err.message || 'Something went wrong. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
            throw err;
        }
    };

    useEffect(() => {
        fetchRingtones();
    }, []);

    // ============================================
    // ===== AUDIO PLAYER FUNCTIONS =====
    // ============================================

    const handlePlayPause = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (audioRef.current && !isDragging) {
            setCurrentTime(audioRef.current.currentTime);
            setDuration(audioRef.current.duration || 0);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration || 0);
            audioRef.current.play();
            setIsPlaying(true);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (audioRef.current) {
            audioRef.current.volume = newVolume;
        }
        if (newVolume === 0) {
            setIsMuted(true);
        } else {
            setIsMuted(false);
        }
    };

    const handleToggleMute = () => {
        if (audioRef.current) {
            audioRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
            if (!isMuted) {
                setVolume(0);
            } else {
                setVolume(0.8);
            }
        }
    };

    const handleSeek = (e) => {
        const progress = progressRef.current;
        if (progress && audioRef.current) {
            const rect = progress.getBoundingClientRect();
            const x = (e.clientX - rect.left) / rect.width;
            const seekTime = x * duration;
            audioRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const handleSeekStart = () => {
        setIsDragging(true);
    };

    const handleSeekEnd = (e) => {
        setIsDragging(false);
        handleSeek(e);
    };

    const handleSpeedChange = () => {
        const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
        const currentIndex = speeds.indexOf(playbackSpeed);
        const nextIndex = (currentIndex + 1) % speeds.length;
        const newSpeed = speeds[nextIndex];
        setPlaybackSpeed(newSpeed);
        if (audioRef.current) {
            audioRef.current.playbackRate = newSpeed;
        }
    };

    const formatTime = (time) => {
        if (isNaN(time)) return "0:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const handlePlayPreview = (row) => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
        setPreviewRingtone(row);
        setPreviewModalOpen(true);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setWaveData(generateWaveData());
    };

    const handleClosePreview = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
        }
        if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
        }
        setPreviewModalOpen(false);
        setPreviewRingtone(null);
        setCurrentTime(0);
        setDuration(0);
        setIsPlaying(false);
        setWaveData([]);
    };

    // ============================================
    // ===== FILE UPLOAD HANDLERS =====
    // ============================================

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const isValidType = AUDIO_TYPES.includes(file.type) ||
            AUDIO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

        if (!isValidType) {
            setFileUploadError('Please upload a valid audio file (MP3, WAV, OGG, AAC, FLAC, M4A, OPUS)');
            return;
        }

        if (file.size > 20 * 1024 * 1024) {
            setFileUploadError('File size exceeds 20MB limit');
            return;
        }

        setFileUploadError(null);
        setFileUploadError(null);
        setIsFileUploading(true);
        setIsFileUploaded(false);

        const fileName = file.name.replace(/\.[^/.]+$/, '');
        setEditForm(prev => ({
            ...prev,
            audioFile: file,
            audioPreview: URL.createObjectURL(file),
            name: fileName
        }));

        try {
            const formData = new FormData();
            formData.append("file", file);

            const base_url = process.env.REACT_APP_ENV === 'local'
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const res = await fetch(`${base_url}/api/r2/upload-audio`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: formData,
            });

            if (!res.ok) throw new Error(`Upload failed ${res.status}`);
            const json = await res.json();
            const data = json.data || json;
            console.log(data);

            const uploadedName = data.name || data.fileName || file.name;
            const uploadedUrl = data.url || data.imageUrl || data.fileUrl;

            setEditForm(prev => ({
                ...prev,
                name: uploadedName,
                audioUrl: uploadedUrl
            }));
            setMediaName(uploadedName);
            setIsFileUploaded(true);
            setIsFileUploading(false);

            Swal.fire({
                icon: 'success',
                title: 'File Uploaded!',
                text: 'Audio file uploaded successfully. You can now create the ringtone.',
                timer: 2000,
                showConfirmButton: false,
            });

            return {
                name: uploadedName,
                url: uploadedUrl,
                fileType: file.type?.startsWith("video") ? "video" : "image",
            };

        } catch (err) {
            console.error('Upload failed:', err);
            setFileUploadError('Failed to upload file. Please try again.');
            setIsFileUploading(false);
            setIsFileUploaded(false);
            
            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: 'Failed to upload audio file. Please try again.',
                confirmButtonColor: '#6366f1',
            });
        }
    };

    const handleRemoveFile = () => {
        if (editForm.audioPreview) {
            URL.revokeObjectURL(editForm.audioPreview);
        }
        setEditForm(prev => ({
            ...prev,
            audioFile: null,
            audioPreview: null,
            audioUrl: null
        }));
        setIsFileUploaded(false);
        setIsFileUploading(false);
        setFileUploadError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // ============================================
    // ===== BULK UPLOAD HANDLERS =====
    // ============================================

    const handleBulkFileUpload = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            const isValidType = AUDIO_TYPES.includes(file.type) ||
                AUDIO_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext));

            if (!isValidType) {
                errors.push(`${file.name}: Invalid file type`);
            } else if (file.size > 20 * 1024 * 1024) {
                errors.push(`${file.name}: File size exceeds 20MB`);
            } else {
                validFiles.push({
                    file: file,
                    name: file.name,
                    preview: URL.createObjectURL(file)
                });
            }
        });

        if (errors.length > 0) {
            setBulkFileError(errors.join(', '));
        } else {
            setBulkFileError(null);
        }

        setBulkFiles(prev => [...prev, ...validFiles]);
        if (bulkFileInputRef.current) {
            bulkFileInputRef.current.value = '';
        }
    };

    const handleRemoveBulkFile = (index) => {
        const fileToRemove = bulkFiles[index];
        if (fileToRemove.preview) {
            URL.revokeObjectURL(fileToRemove.preview);
        }
        setBulkFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleBulkSubmit = async () => {
        if (bulkFiles.length === 0) {
            setBulkFileError('Please select at least one audio file');
            return;
        }

        setBulkUploadLoading(true);
        try {
            const results = [];
            for (const fileData of bulkFiles) {
                const newData = {
                    name: fileData.file.name.replace(/\.[^/.]+$/, ''),
                    category: bulkForm.category,
                    sub_category: bulkForm.subCategory,
                    artist: bulkForm.artist,
                    display_name: bulkForm.disoplayName || fileData.file.name.replace(/\.[^/.]+$/, ''),
                    tags: bulkForm.tags,
                    type: "audio",
                    status: bulkForm.status,
                    duration: "0:00",
                    url: fileData.preview,
                };
                
                const result = await createRingtone(newData);
                results.push(result);
            }

            setBulkFiles([]);
            setBulkUploadOpen(false);
            
            Swal.fire({
                icon: 'success',
                title: 'Bulk Upload Complete!',
                text: `${results.length} audio files uploaded successfully!`,
                timer: 3000,
                showConfirmButton: false,
            });

        } catch (err) {
            console.error('Failed to bulk upload:', err);
            Swal.fire({
                icon: 'error',
                title: 'Bulk Upload Failed',
                text: 'Failed to upload some files. Please try again.',
                confirmButtonColor: '#6366f1',
            });
        } finally {
            setBulkUploadLoading(false);
        }
    };

    const getCategoryIcon = (category) => {
        const icons = {
            ringtone: <FiHeadphones size={14} />,
            mantra: <FiMic size={14} />,
            shloka: <FiMusic size={14} />,
            chalisa: <FiMusic size={14} />,
            bhajan: <FiMusic size={14} />,
            aarti: <FiMusic size={14} />,
            stotram: <FiMusic size={14} />,
            kirtan: <FiMusic size={14} />,
            meditation: <FiMusic size={14} />,
            nature: <FiMusic size={14} />,
        };
        return icons[category] || <FiMusic size={14} />;
    };

    // ============================================
    // ===== COLUMNS DEFINITION =====
    // ============================================

    const columns = [
        {
            name: (
                <input
                    type="checkbox"
                    checked={filteredData.length > 0 && filteredData.every(item => selectedIds.includes(item._id))}
                    onChange={() => {
                        const allIds = filteredData.map(item => item._id);
                        const allSelected = allIds.every(id => selectedIds.includes(id));
                        if (allSelected) {
                            setSelectedIds([]);
                        } else {
                            setSelectedIds(allIds);
                        }
                    }}
                    disabled={bulkActionLoading || filteredData.length === 0}
                    className="checkbox-custom"
                />
            ),
            cell: row => (
                <input
                    type="checkbox"
                    checked={selectedIds.includes(row._id)}
                    onChange={() => {
                        setSelectedIds(prev => {
                            if (prev.includes(row._id)) {
                                return prev.filter(item => item !== row._id);
                            } else {
                                return [...prev, row._id];
                            }
                        });
                    }}
                    disabled={bulkActionLoading}
                    className="checkbox-custom"
                />
            ),
            width: "50px",
        },
        {
            name: "Play",
            cell: row => (
                <button
                    className="play-btn"
                    onClick={() => handlePlayPreview(row)}
                    title="Play audio"
                >
                    <FiPlay size={16} />
                </button>
            ),
            width: "60px",
        },
        {
            name: "Name",
            selector: row => row.name,
            sortable: true,
            cell: row => <span className="ringtone-name">{row.name}</span>,
        },
        {
            name: "Category",
            selector: row => row.category,
            sortable: true,
            cell: row => (
                <span className={`category-badge ${row.category}`}>
                    {getCategoryIcon(row.category)}
                    {row.category}
                </span>
            ),
        },
        {
            name: "Sub Category",
            selector: row => row.subCategory || "-",
            sortable: true,
            cell: row => <span className="sub-category">{row.subCategory || "-"}</span>,
        },
        {
            name: "Artist",
            selector: row => row.artist || "-",
            sortable: true,
            cell: row => <span className="ringtone-artist">{row.artist || "-"}</span>,
        },
        {
            name: "Duration",
            selector: row => row.duration || "-",
            sortable: true,
            cell: row => <span className="ringtone-duration">{row.duration || "-"}</span>,
        },
        {
            name: "Status",
            cell: row => {
                const isUpdating = updatingStatusId === row._id;

                return (
                    <div className="status-select-wrapper">
                        <select
                            className={`status-select ${row.status || 'unpublished'}`}
                            value={row.status || "unpublished"}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            disabled={isUpdating}
                        >
                            <option value="published">Published</option>
                            <option value="unpublished">Unpublished</option>
                        </select>
                        {isUpdating && (
                            <span className="status-updating">Updating...</span>
                        )}
                    </div>
                );
            },
            sortable: true,
        },
        {
            name: "Actions",
            cell: row => (
                <div className="action-buttons">
                    <button 
                        className="action-btn edit" 
                        onClick={() => openEditModal(row)}
                        title="Edit"
                    >
                        <FiEdit size={14} />
                    </button>
                    <button 
                        className="action-btn delete" 
                        onClick={() => handleDelete(row._id)}
                        title="Delete"
                    >
                        <FiTrash2 size={14} />
                    </button>
                </div>
            ),
        },
    ];

    // ============================================
    // ===== HANDLE STATUS CHANGE =====
    // ============================================

    const handleStatusChange = async (row, newStatus) => {
        const previousStatus = row.status;
        setUpdatingStatusId(row._id);
        setRingtoneData((prev) =>
            prev.map((item) =>
                item._id === row._id ? { ...item, status: newStatus } : item
            )
        );

        try {
            const apiBaseUrl = process.env.REACT_APP_ENV === "local"
                ? process.env.REACT_APP_BACKEND_API_LOCAL
                : process.env.REACT_APP_BACKEND_API;

            const response = await fetch(`${apiBaseUrl}/api/home/admin-media-data/${row._id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${process.env.REACT_APP_ADMIN_MEDIA_TOKEN || ''}`,
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (!response.ok) throw new Error(`Update failed ${response.status}`);

            await fetchRingtones();

        } catch (err) {
            console.error('Failed to update ringtone status:', err);
            setRingtoneData((prev) =>
                prev.map((item) =>
                    item._id === row._id ? { ...item, status: previousStatus } : item
                )
            );
            
            Swal.fire({
                icon: 'error',
                title: 'Status Update Failed',
                text: 'Failed to update status. Please try again.',
                confirmButtonColor: '#6366f1',
            });
            
        } finally {
            setUpdatingStatusId(null);
        }
    };

    // ============================================
    // ===== HANDLE BULK ACTION =====
    // ============================================

    const handleBulkAction = async (action) => {
        if (selectedIds.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No Selection',
                text: 'Please select at least one item.',
                confirmButtonColor: '#6366f1',
            });
            return;
        }

        let confirmMessage = "";
        let actionText = "";

        switch (action) {
            case "publish":
                confirmMessage = `Are you sure you want to publish ${selectedIds.length} audio file(s)?`;
                actionText = "publish";
                break;
            case "unpublish":
                confirmMessage = `Are you sure you want to unpublish ${selectedIds.length} audio file(s)?`;
                actionText = "unpublish";
                break;
            case "delete":
                confirmMessage = `Are you sure you want to delete ${selectedIds.length} audio file(s)? This action cannot be undone.`;
                actionText = "delete";
                break;
            default:
                return;
        }

        const result = await Swal.fire({
            title: 'Confirm Action',
            text: confirmMessage,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: action === 'delete' ? '#ef4444' : '#6366f1',
            cancelButtonColor: '#6b7280',
            confirmButtonText: `Yes, ${actionText} them!`,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        setBulkActionLoading(true);
        try {
            if (action === "delete") {
                await bulkDeleteRingtones(selectedIds);
            } else {
                await bulkUpdateStatus(selectedIds, action === "publish" ? "published" : "unpublished");
            }
            setSelectedIds([]);
        } catch (err) {
            console.error(`Failed to perform bulk ${action}:`, err);
            Swal.fire({
                icon: 'error',
                title: 'Action Failed',
                text: `Failed to ${action} items. Please try again.`,
                confirmButtonColor: '#6366f1',
            });
        } finally {
            setBulkActionLoading(false);
        }
    };

    // ============================================
    // ===== MODAL HANDLERS =====
    // ============================================

    const openEditModal = (row) => {
        setEditingRingtone(row);
        setEditForm({
            name: row.name || "",
            category: row.category || "ringtone",
            subCategory: row.subCategory || "devotional",
            tags: Array.isArray(row.tags) ? row.tags : [],
            status: row.status || "unpublished",
            duration: row.duration || "",
            artist: row.artist || "",
            audioFile: null,
            audioPreview: null,
            audioUrl: null
        });
        setFileUploadError(null);
        setIsFileUploaded(false);
        setIsFileUploading(false);
        setEditModalOpen(true);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setEditingRingtone(null);
        setEditForm({
            name: "",
            category: "ringtone",
            subCategory: "devotional",
            tags: [],
            status: "unpublished",
            duration: "",
            artist: "",
            audioFile: null,
            audioPreview: null,
            audioUrl: null
        });
        setFileUploadError(null);
        setIsFileUploaded(false);
        setIsFileUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!editingRingtone?._id) return;

        try {
            setEditLoading(true);
            const updateData = {
                display_name: editForm.name,
                category: editForm.category,
                sub_category: editForm.subCategory,
                tags: editForm.tags,
                status: editForm.status,
                duration: editForm.duration,
                artist: editForm.artist,
                type: "audio",
            };

            if (editForm.audioFile) {
                updateData.url = URL.createObjectURL(editForm.audioFile);
            }

            await updateRingtone(editingRingtone._id, updateData);
            closeEditModal();
        } catch (err) {
            console.error('Failed to update ringtone:', err);
            Swal.fire({
                icon: 'error',
                title: 'Update Failed',
                text: 'Failed to update audio file. Please try again.',
                confirmButtonColor: '#6366f1',
            });
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Audio?',
            text: 'This action cannot be undone!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await deleteRingtone(id);
        } catch (err) {
            console.error('Failed to delete ringtone:', err);
        }
    };

    const handleAddRingtone = () => {
        setEditingRingtone(null);
        setEditForm({
            name: "",
            category: "ringtone",
            subCategory: "devotional",
            tags: [],
            status: "unpublished",
            duration: "",
            artist: "",
            audioFile: null,
            audioPreview: null,
            audioUrl: null
        });
        setFileUploadError(null);
        setIsFileUploaded(false);
        setIsFileUploading(false);
        setShow(true);
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        
        if (!editForm.audioFile) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing File',
                text: 'Please upload an audio file.',
                confirmButtonColor: '#6366f1',
            });
            setFileUploadError('Please upload an audio file');
            return;
        }

        if (!isFileUploaded) {
            Swal.fire({
                icon: 'warning',
                title: 'Upload Pending',
                text: 'Please wait for the file to finish uploading.',
                confirmButtonColor: '#6366f1',
            });
            return;
        }

        try {
            setEditLoading(true);
            
            const newData = {
                name: editForm.name,
                display_name: editForm.display_name,
                category: editForm.category,
                sub_category: editForm.subCategory,
                tags: editForm.tags,
                type: "audio",
                status: editForm.status,
                duration: editForm.duration,
                artist: editForm.artist,
                url: editForm.audioUrl || URL.createObjectURL(editForm.audioFile),
            };

            await createRingtone(newData);
            setShow(false);
            setEditForm({
                name: "",
                display_name: "",
                category: "ringtone",
                subCategory: "devotional",
                tags: [],
                status: "unpublished",
                duration: "",
                artist: "",
                audioFile: null,
                audioPreview: null,
                audioUrl: null
            });
            setFileUploadError(null);
            setIsFileUploaded(false);
            setIsFileUploading(false);

        } catch (err) {
            console.error('Failed to create ringtone:', err);
        } finally {
            setEditLoading(false);
        }
    };

    // ============================================
    // ===== RENDER =====
    // ============================================

    return (
        <div className={`ringtones-container ${darkMode ? 'dark' : ''}`}>
            <div className="ringtones-content">
                {/* Page Header */}
                <div className="page-header-wrapper">
                    <div className="header-left">
                        <h1 className="page-title">Ringtones & Devotional Audio</h1>
                        <p className="page-subtitle">Manage ringtones, mantras, shlokas, chalishas, bhajans and more</p>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn-bulk-upload"
                            onClick={() => setBulkUploadOpen(true)}
                        >
                            <FiUpload size={16} />
                            Bulk Upload
                        </button>
                        <button
                            className="btn-add-ringtone"
                            onClick={handleAddRingtone}
                        >
                            <FiPlus size={16} />
                            Add Audio
                        </button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon total">
                            <FiMusic size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Total Audio</span>
                            <span className="stat-value">{totalRingtones}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon published">
                            <FiEye size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Published</span>
                            <span className="stat-value">{publishedCount}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon unpublished">
                            <FiEyeOff size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Unpublished</span>
                            <span className="stat-value">{unpublishedCount}</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon categories">
                            <FiFile size={20} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-label">Categories</span>
                            <span className="stat-value">{categories.length}</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="table-card-modern">
                    {/* Toolbar */}
                    <div className="table-toolbar">
                        <div className="search-wrapper">
                            <FiSearch className="search-icon" />
                            <input
                                className="search-input-modern"
                                placeholder="Search by name, category, sub-category, artist, or tags..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="toolbar-actions">
                            {selectedIds.length > 0 && (
                                <div className="bulk-actions">
                                    <span className="selected-count">
                                        <FiCheck size={14} />
                                        {selectedIds.length} selected
                                    </span>
                                    <button
                                        className="bulk-action-btn publish"
                                        onClick={() => handleBulkAction("publish")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiEye size={14} />
                                        Publish
                                    </button>
                                    <button
                                        className="bulk-action-btn unpublish"
                                        onClick={() => handleBulkAction("unpublish")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiEyeOff size={14} />
                                        Unpublish
                                    </button>
                                    <button
                                        className="bulk-action-btn delete"
                                        onClick={() => handleBulkAction("delete")}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiTrash2 size={14} />
                                        Delete
                                    </button>
                                    <button
                                        className="bulk-action-btn clear"
                                        onClick={() => setSelectedIds([])}
                                        disabled={bulkActionLoading}
                                    >
                                        <FiX size={14} />
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="table-wrapper-modern">
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner"></div>
                                <p>Loading audio...</p>
                            </div>
                        ) : (
                            <CustomDataTable
                                columns={columns}
                                data={filteredData}
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="table-footer-modern">
                        <span className="table-info">
                            Showing {filteredData.length} of {ringtoneData.length} entries
                        </span>
                        <div className="pagination-modern">
                            <button className="page-btn" disabled>
                                <FiChevronLeft size={16} />
                            </button>
                            <button className="page-btn active">1</button>
                            <button className="page-btn">2</button>
                            <button className="page-btn">3</button>
                            <button className="page-btn">
                                <FiChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ===== ADD AUDIO MODAL - LARGE ===== */}
            <Modal show={show} onHide={() => setShow(false)} centered size="xl" className={`edit-modal ${darkMode ? 'dark' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Audio</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Audio File *</Form.Label>
                                    <div className="file-upload-wrapper">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="audio/*"
                                            onChange={handleFileUpload}
                                            className="file-input-hidden"
                                            id="audio-file-upload"
                                        />
                                        <label htmlFor="audio-file-upload" className="file-upload-label">
                                            {editForm.audioFile ? (
                                                <div className="file-selected">
                                                    <FiMusic size={20} />
                                                    <span>{editForm.audioFile.name}</span>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={handleRemoveFile}
                                                        disabled={isFileUploading}
                                                    >
                                                        <FiX size={16} />
                                                    </button>
                                                    {isFileUploading && (
                                                        <span className="uploading-status">Uploading...</span>
                                                    )}
                                                    {isFileUploaded && (
                                                        <span className="uploaded-status">✓ Uploaded</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="file-drop-zone">
                                                    <FiUpload size={24} />
                                                    <span>Click to upload or drag & drop</span>
                                                    <small>Supported: MP3, WAV, OGG, AAC, FLAC, M4A, OPUS (Max 20MB)</small>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                    {fileUploadError && (
                                        <div className="file-error">{fileUploadError}</div>
                                    )}
                                    {editForm.audioPreview && (
                                        <div className="audio-preview">
                                            <audio src={editForm.audioPreview} controls style={{ width: '100%' }} />
                                        </div>
                                    )}
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                        required
                                        readOnly
                                        className="form-control-modern"
                                        placeholder="Enter audio name"
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Display Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.display_name}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, display_name: e.target.value }))}
                                        required
                                        className="form-control-modern"
                                        placeholder="Enter audio display name"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Artist</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.artist}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, artist: e.target.value }))}
                                        className="form-control-modern"
                                        placeholder="Enter artist name"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Duration</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.duration}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, duration: e.target.value }))}
                                        className="form-control-modern"
                                        placeholder="e.g., 3:45"
                                    />
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Category *</Form.Label>
                                    <Form.Select
                                        value={editForm.category}
                                        onChange={(e) => {
                                            const newCategory = e.target.value;
                                            setEditForm((prev) => ({
                                                ...prev,
                                                category: newCategory,
                                                subCategory: subCategoryOptions[newCategory]?.[0]?.value || ""
                                            }));
                                        }}
                                        required
                                        className="form-control-modern"
                                    >
                                        {categoryOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Sub Category *</Form.Label>
                                    <Form.Select
                                        value={editForm.subCategory}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, subCategory: e.target.value }))}
                                        required
                                        className="form-control-modern"
                                    >
                                        {subCategoryOptions[editForm.category]?.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                                        className="form-control-modern"
                                    >
                                        <option value="published">Published</option>
                                        <option value="unpublished">Unpublished</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tags</Form.Label>
                                    <TagsInput
                                        value={editForm.tags}
                                        onChange={(tags) => setEditForm((prev) => ({ ...prev, tags }))}
                                        placeholder="Add tags and press Enter"
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <Button variant="secondary" onClick={() => setShow(false)} disabled={editLoading}>
                                Cancel
                            </Button>
                            <Button 
                                variant="primary" 
                                type="submit" 
                                disabled={editLoading || !editForm.audioFile || isFileUploading || !isFileUploaded}
                            >
                                {editLoading ? "Creating..." : 
                                 isFileUploading ? "Uploading File..." : 
                                 !editForm.audioFile ? "Select File" : 
                                 !isFileUploaded ? "Waiting for Upload..." : 
                                 "Create Audio"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ===== EDIT MODAL - LARGE ===== */}
            <Modal show={editModalOpen} onHide={closeEditModal} centered size="xl" className={`edit-modal ${darkMode ? 'dark' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Audio</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleEditSubmit}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.name}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))}
                                        required
                                        className="form-control-modern"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Artist</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.artist}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, artist: e.target.value }))}
                                        className="form-control-modern"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Duration</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={editForm.duration}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, duration: e.target.value }))}
                                        className="form-control-modern"
                                        placeholder="e.g., 3:45"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Replace Audio File</Form.Label>
                                    <div className="file-upload-wrapper">
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            onChange={handleFileUpload}
                                            className="file-input-hidden"
                                            id="edit-audio-file"
                                        />
                                        <label htmlFor="edit-audio-file" className="file-upload-label">
                                            {editForm.audioFile ? (
                                                <div className="file-selected">
                                                    <FiMusic size={20} />
                                                    <span>{editForm.audioFile.name}</span>
                                                    <button
                                                        type="button"
                                                        className="remove-file-btn"
                                                        onClick={handleRemoveFile}
                                                        disabled={isFileUploading}
                                                    >
                                                        <FiX size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="file-drop-zone">
                                                    <FiUpload size={24} />
                                                    <span>Click to upload new audio file</span>
                                                    <small>Leave empty to keep existing</small>
                                                </div>
                                            )}
                                        </label>
                                    </div>
                                    {fileUploadError && (
                                        <div className="file-error">{fileUploadError}</div>
                                    )}
                                    {editForm.audioPreview && (
                                        <div className="audio-preview">
                                            <audio src={editForm.audioPreview} controls style={{ width: '100%' }} />
                                        </div>
                                    )}
                                </Form.Group>
                            </div>

                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Category *</Form.Label>
                                    <Form.Select
                                        value={editForm.category}
                                        onChange={(e) => {
                                            const newCategory = e.target.value;
                                            setEditForm((prev) => ({
                                                ...prev,
                                                category: newCategory,
                                                subCategory: subCategoryOptions[newCategory]?.[0]?.value || ""
                                            }));
                                        }}
                                        required
                                        className="form-control-modern"
                                    >
                                        {categoryOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Sub Category *</Form.Label>
                                    <Form.Select
                                        value={editForm.subCategory}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, subCategory: e.target.value }))}
                                        required
                                        className="form-control-modern"
                                    >
                                        {subCategoryOptions[editForm.category]?.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                                        className="form-control-modern"
                                    >
                                        <option value="published">Published</option>
                                        <option value="unpublished">Unpublished</option>
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Tags</Form.Label>
                                    <TagsInput
                                        value={editForm.tags}
                                        onChange={(tags) => setEditForm((prev) => ({ ...prev, tags }))}
                                        placeholder="Add tags and press Enter"
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <Button variant="secondary" onClick={closeEditModal} disabled={editLoading}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={editLoading}>
                                {editLoading ? "Saving..." : "Save Changes"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* ===== BULK UPLOAD MODAL - LARGE ===== */}
            <Modal show={bulkUploadOpen} onHide={() => setBulkUploadOpen(false)} centered size="xl" className={`edit-modal ${darkMode ? 'dark' : ''}`}>
                <Modal.Header closeButton>
                    <Modal.Title>Bulk Upload Audio</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>Upload Audio Files *</Form.Label>
                                <div className="file-upload-wrapper">
                                    <input
                                        ref={bulkFileInputRef}
                                        type="file"
                                        accept="audio/*"
                                        onChange={handleBulkFileUpload}
                                        className="file-input-hidden"
                                        id="bulk-audio-file-upload"
                                        multiple
                                    />
                                    <label htmlFor="bulk-audio-file-upload" className="file-upload-label">
                                        <div className="file-drop-zone">
                                            <FiUpload size={32} />
                                            <span>Click to upload multiple audio files</span>
                                            <small>Supported: MP3, WAV, OGG, AAC, FLAC, M4A, OPUS (Max 20MB each)</small>
                                        </div>
                                    </label>
                                </div>
                                {bulkFileError && (
                                    <div className="file-error">{bulkFileError}</div>
                                )}
                            </Form.Group>

                            {bulkFiles.length > 0 && (
                                <div className="bulk-file-list">
                                    <div className="bulk-file-header">
                                        <span>{bulkFiles.length} files selected</span>
                                        <div className="bulk-view-toggle">
                                            <button
                                                className={`view-toggle-btn ${bulkViewMode === 'grid' ? 'active' : ''}`}
                                                onClick={() => setBulkViewMode('grid')}
                                            >
                                                <FiGrid size={14} />
                                            </button>
                                            <button
                                                className={`view-toggle-btn ${bulkViewMode === 'list' ? 'active' : ''}`}
                                                onClick={() => setBulkViewMode('list')}
                                            >
                                                <FiList size={14} />
                                            </button>
                                        </div>
                                    </div>

                                    {bulkViewMode === 'grid' ? (
                                        <div className="bulk-file-grid">
                                            {bulkFiles.map((fileData, index) => (
                                                <div key={index} className="bulk-file-card">
                                                    <div className="bulk-file-preview">
                                                        <FiMusic size={24} />
                                                    </div>
                                                    <div className="bulk-file-info">
                                                        <span className="bulk-file-name">{fileData.file.name}</span>
                                                        <span className="bulk-file-size">
                                                            {(fileData.file.size / (1024 * 1024)).toFixed(2)} MB
                                                        </span>
                                                    </div>
                                                    <button
                                                        className="bulk-file-remove"
                                                        onClick={() => handleRemoveBulkFile(index)}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="bulk-file-list-view">
                                            {bulkFiles.map((fileData, index) => (
                                                <div key={index} className="bulk-file-item">
                                                    <FiMusic size={16} />
                                                    <span className="bulk-file-name">{fileData.file.name}</span>
                                                    <span className="bulk-file-size">
                                                        {(fileData.file.size / (1024 * 1024)).toFixed(2)} MB
                                                    </span>
                                                    <button
                                                        className="bulk-file-remove"
                                                        onClick={() => handleRemoveBulkFile(index)}
                                                    >
                                                        <FiX size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="col-md-6">
                            <Form.Group className="mb-3">
                                <Form.Label>Category *</Form.Label>
                                <Form.Select
                                    value={bulkForm.category}
                                    onChange={(e) => {
                                        const newCategory = e.target.value;
                                        setBulkForm((prev) => ({
                                            ...prev,
                                            category: newCategory,
                                            subCategory: subCategoryOptions[newCategory]?.[0]?.value || ""
                                        }));
                                    }}
                                    required
                                    className="form-control-modern"
                                >
                                    {categoryOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Sub Category *</Form.Label>
                                <Form.Select
                                    value={bulkForm.subCategory}
                                    onChange={(e) => setBulkForm((prev) => ({ ...prev, subCategory: e.target.value }))}
                                    required
                                    className="form-control-modern"
                                >
                                    {subCategoryOptions[bulkForm.category]?.map(opt => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Artist (Optional)</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={bulkForm.artist}
                                    onChange={(e) => setBulkForm((prev) => ({ ...prev, artist: e.target.value }))}
                                    className="form-control-modern"
                                    placeholder="Enter artist name for all files"
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={bulkForm.status}
                                    onChange={(e) => setBulkForm((prev) => ({ ...prev, status: e.target.value }))}
                                    className="form-control-modern"
                                >
                                    <option value="published">Published</option>
                                    <option value="unpublished">Unpublished</option>
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Tags (Applied to all)</Form.Label>
                                <TagsInput
                                    value={bulkForm.tags}
                                    onChange={(tags) => setBulkForm((prev) => ({ ...prev, tags }))}
                                    placeholder="Add tags and press Enter"
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="modal-actions">
                        <Button variant="secondary" onClick={() => setBulkUploadOpen(false)} disabled={bulkUploadLoading}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleBulkSubmit} disabled={bulkUploadLoading || bulkFiles.length === 0}>
                            {bulkUploadLoading ? "Uploading..." : `Upload ${bulkFiles.length} Files`}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* ===== PREVIEW MODAL ===== */}
            <Modal
                show={previewModalOpen}
                onHide={handleClosePreview}
                centered
                size={isExpanded ? "xl" : "lg"}
                className={`preview-modal ${darkMode ? 'dark' : ''} ${isExpanded ? 'expanded' : ''}`}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FiMusic size={18} style={{ marginRight: 10 }} />
                        {previewRingtone?.name || "Audio Preview"}
                    </Modal.Title>
                    <button
                        className="expand-btn"
                        onClick={() => setIsExpanded(!isExpanded)}
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        {isExpanded ? <FiMinimize2 size={18} /> : <FiMaximize2 size={18} />}
                    </button>
                </Modal.Header>
                <Modal.Body className="preview-modal-body">
                    {previewRingtone && (
                        <div className="audio-player-container">
                            <audio
                                ref={audioRef}
                                src={previewRingtone.url}
                                onTimeUpdate={handleTimeUpdate}
                                onLoadedMetadata={handleLoadedMetadata}
                                onEnded={() => {
                                    if (isRepeat) {
                                        audioRef.current.currentTime = 0;
                                        audioRef.current.play();
                                    } else {
                                        setIsPlaying(false);
                                        setCurrentTime(0);
                                    }
                                }}
                                className="audio-element"
                            />

                            <div className="audio-artwork">
                                <div className="artwork-icon">
                                    <FiMusic size={isExpanded ? 48 : 32} />
                                </div>
                            </div>

                            <div className="track-info">
                                <h4 className="track-title">{previewRingtone.name}</h4>
                                <p className="track-artist">{previewRingtone.artist || "Unknown Artist"}</p>
                                <p className="track-category">
                                    <span className={`category-badge ${previewRingtone.category}`}>
                                        {previewRingtone.category}
                                        {previewRingtone.subCategory && ` • ${previewRingtone.subCategory}`}
                                    </span>
                                </p>
                            </div>

                            <div className="wave-container">
                                <canvas
                                    ref={canvasRef}
                                    className="wave-canvas"
                                    width={600}
                                    height={80}
                                />
                                <div
                                    ref={progressRef}
                                    className="wave-progress-overlay"
                                    onMouseDown={handleSeekStart}
                                    onMouseUp={handleSeekEnd}
                                    onMouseMove={(e) => {
                                        if (isDragging) {
                                            handleSeek(e);
                                        }
                                    }}
                                    onClick={handleSeek}
                                />
                            </div>

                            <div className="time-display">
                                <span className="time-current">{formatTime(currentTime)}</span>
                                <span className="time-duration">{formatTime(duration)}</span>
                            </div>

                            <div className="controls-container">
                                <button
                                    className="control-btn shuffle"
                                    onClick={() => setIsShuffle(!isShuffle)}
                                    title="Shuffle"
                                >
                                    <FiShuffle size={18} className={isShuffle ? 'active' : ''} />
                                </button>

                                <button
                                    className="control-btn skip-back"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
                                        }
                                    }}
                                    title="Skip Back 10s"
                                >
                                    <FiSkipBack size={20} />
                                </button>

                                <button
                                    className="control-btn play-pause"
                                    onClick={handlePlayPause}
                                >
                                    {isPlaying ? <FiPause size={28} /> : <FiPlay size={28} />}
                                </button>

                                <button
                                    className="control-btn skip-forward"
                                    onClick={() => {
                                        if (audioRef.current) {
                                            audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
                                        }
                                    }}
                                    title="Skip Forward 10s"
                                >
                                    <FiSkipForward size={20} />
                                </button>

                                <button
                                    className="control-btn repeat"
                                    onClick={() => setIsRepeat(!isRepeat)}
                                    title="Repeat"
                                >
                                    <FiRepeat size={18} className={isRepeat ? 'active' : ''} />
                                </button>
                            </div>

                            <div className="bottom-controls">
                                <div className="volume-control">
                                    <button
                                        className="volume-btn"
                                        onClick={handleToggleMute}
                                        title={isMuted ? "Unmute" : "Mute"}
                                    >
                                        {isMuted || volume === 0 ? <FiVolumeX size={18} /> : <FiVolume2 size={18} />}
                                    </button>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.01"
                                        value={isMuted ? 0 : volume}
                                        onChange={handleVolumeChange}
                                        className="volume-slider"
                                        style={{
                                            background: `linear-gradient(to right, #6366f1 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.1) ${(isMuted ? 0 : volume) * 100}%)`
                                        }}
                                    />
                                </div>

                                <button
                                    className="speed-btn"
                                    onClick={handleSpeedChange}
                                    title={`Speed: ${playbackSpeed}x`}
                                >
                                    {playbackSpeed}x
                                </button>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}